import { Link } from 'react-router-dom';
import type { UserInfo } from '../services/api';

interface LandingProps {
  user?: UserInfo | null;
}

export function Landing({ user }: LandingProps) {
  const targetUrl = user?.authenticated
    ? (user.user?.organizationId ? '/dashboard' : '/onboarding')
    : '/auth/login';

  return (
    <div className="landing">


      {/* ── Header ─────────────────────────────────────────── */}
      <header className="landing-header">
        <nav className="landing-nav">
          <div className="landing-nav-brand">
            <img
              src="/favicon/android-chrome-192x192.png"
              alt="SealVision logo"
              width={28}
              height={28}
              style={{ borderRadius: 6 }}
            />
            SealVision
          </div>
          <div className="landing-nav-actions">
            {user?.authenticated ? (
              <>
                <span className="text-sm text-muted" style={{ marginRight: 8, display: 'inline-flex', alignItems: 'center' }}>
                  {user.user?.name || user.user?.email}
                </span>
                <Link to={targetUrl} className="btn btn-primary">
                  {user.user?.organizationId ? 'Dashboard' : 'Complete Setup'}
                </Link>
                <a href="/auth/logout" className="btn btn-secondary" style={{ padding: '6px 12px' }}>
                  Sign Out
                </a>
              </>
            ) : (
              <>
                <a href="/auth/login" className="btn btn-secondary">Sign In</a>
                <a href="/auth/login" className="btn btn-primary">Get Started</a>
              </>
            )}
          </div>
        </nav>
      </header>

      {/* ── 1. HOOK — The hero statement ──────────────────────── */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <h1>
            Every seal your line produces.<br />
            <span className="accent">Every single one, inspected.</span>
          </h1>
          <p className="lead">
            SealVision sees what cameras and operators cannot — thermal anomalies
            trapped inside heat seals — and catches them in real time, before a
            defective package ever leaves your facility.
          </p>
          <div className="landing-hero-actions">
            {user?.authenticated ? (
              <Link to={targetUrl} className="btn btn-primary btn-lg">
                {user.user?.organizationId ? 'Open Dashboard' : 'Complete Setup'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
            ) : (
              <a href="/auth/login" className="btn btn-primary btn-lg">
                Start Monitoring
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            )}
            <a href="#the-problem" className="btn btn-secondary btn-lg">
              See how it works
            </a>
          </div>
        </div>
        <div className="landing-hero-image">
          <img
            src="/images/hero-thermal-split.jpg"
            alt="Split view: a sealed pouch under normal light (left) and thermal imaging revealing hidden contamination in the seal area (right)"
            width="600"
            height="400"
          />
        </div>
      </section>

      {/* ── 2. THE PROBLEM — The blind spot ────────────────── */}
      <section className="landing-section alt-bg" id="the-problem">
        <div className="section-inner">
          <div className="problem-split">
            <div>
              <div className="section-eyebrow">The Problem</div>
              <h2 className="section-heading">
                Between your QA checks, thousands of packages leave unchecked
              </h2>
              <p className="section-subheading">
                Your packaging line runs at 100–1,200 units per minute. Quality
                verification happens through destructive sampling — an operator
                pulls a handful of packages every few hours for peel tests and
                vacuum decay checks. Between those samples, the line runs blind.
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: 16 }}>
                If a sealing jaw drifts out of spec, or product contaminates the
                seal area, defective packages keep flowing downstream until
                someone discovers the problem at the next check. By then, an
                entire batch may be compromised — forcing quarantine, rework, or
                recall.
              </p>
              <div className="problem-stats">
                <div className="problem-stat">
                  <div className="value">3–5%</div>
                  <div className="label">False reject rate from legacy RGB vision systems</div>
                </div>
                <div className="problem-stat">
                  <div className="value">50,000</div>
                  <div className="label">Good packages wasted daily on a 1M-unit line</div>
                </div>
                <div className="problem-stat">
                  <div className="value">$800K+</div>
                  <div className="label">Annual cost of scrap and labor for a 4-line plant</div>
                </div>
                <div className="problem-stat">
                  <div className="value">Hours</div>
                  <div className="label">Feedback delay from destructive lab sampling</div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/problem-blindspot.jpg"
                alt="Factory floor showing a high-speed packaging line with an operator performing manual quality checks — the gap between continuous production and intermittent inspection"
                width="600"
                height="400"
                style={{ borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-lg)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. THE RESOLUTION — SealVision enters ──────────── */}
      <section className="landing-section">
        <div className="section-inner" style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
          <div className="section-eyebrow">The Resolution</div>
          <h2 className="section-heading" style={{ maxWidth: 600, margin: '0 auto var(--space-4)' }}>
            Close the blind spot with thermal intelligence
          </h2>
          <p className="section-subheading" style={{ margin: '0 auto var(--space-6)' }}>
            SealVision mounts a thermal imaging sensor directly downstream of your
            sealing jaws. As each package passes, it captures the cooling profile
            of the seal — and AI trained on your own "golden samples" instantly
            flags any deviation. No destructive testing. No sampling gap. Every
            package, every time.
          </p>
        </div>

        <div className="kpi-strip">
          {[
            { value: '100%', label: 'Inline coverage' },
            { value: '< 0.3%', label: 'False reject rate' },
            { value: '< 50ms', label: 'Detection-to-reject latency' },
            { value: '99.9%', label: 'System uptime' },
          ].map((kpi) => (
            <div className="kpi-item" key={kpi.label}>
              <div className="kpi-value">{kpi.value}</div>
              <div className="kpi-label">{kpi.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 4. HOW IT WORKS — The 3-step pipeline ──────────── */}
      <section className="landing-section alt-bg" id="how-it-works">
        <div className="section-inner">
          <div className="section-eyebrow">How It Works</div>
          <h2 className="section-heading">From thermal signature to automated decision</h2>
          <p className="section-subheading">
            Three steps, all completed within 50 milliseconds — fast enough to
            trigger a pneumatic reject mechanism before the package moves
            downstream.
          </p>

          <div className="how-steps">
            <div className="how-step">
              <div className="how-step-number">1</div>
              <h3>Capture the cooling profile</h3>
              <p>
                The moment the sealing jaws release, a LWIR thermal camera records
                the transient cooling curve of the seal. Contaminants, wrinkles,
                and temperature deviations alter thermal diffusivity — creating a
                distinct signature invisible to RGB cameras.
              </p>
            </div>
            <div className="how-step">
              <div className="how-step-number">2</div>
              <h3>AI compares against your baseline</h3>
              <p>
                A temporal fusion network running on edge hardware compares the
                thermal signature against your golden sample baseline — trained on
                30–50 known-good seals from your own line. No massive labeled
                datasets required.
              </p>
            </div>
            <div className="how-step">
              <div className="how-step-number">3</div>
              <h3>Accept, flag, or reject — instantly</h3>
              <p>
                A clean seal passes through. An anomaly triggers a hardwired 24V
                signal to your PLC, actuating a pneumatic reject mechanism within
                50ms. Every result is logged with thermal metrics, images, and
                timestamps for full traceability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. THE EDGE DEVICE — Hardware meets intelligence ── */}
      <section className="landing-section">
        <div className="section-inner">
          <div className="device-split">
            <div>
              <img
                src="/images/how-edge-device.jpg"
                alt="SealVision edge computing device with thermal camera mounted on a packaging line, seamlessly integrated into factory infrastructure"
                width="600"
                height="400"
              />
            </div>
            <div>
              <div className="section-eyebrow">Built for the Factory Floor</div>
              <h2 className="section-heading">
                Edge intelligence that runs without the cloud
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: 24 }}>
                The SealVision appliance is a self-contained edge system — no
                internet dependency for critical inspection. It runs in an
                IP67-rated enclosure, processes data locally, and communicates
                with your PLC over hardwired digital I/O for deterministic
                sub-millisecond timing.
              </p>
              <div className="device-specs">
                {[
                  { icon: '🌡️', title: 'LWIR Thermal Camera', desc: '640×480 uncooled microbolometer, 125 Hz frame rate' },
                  { icon: '⚡', title: 'NVIDIA Jetson Orin', desc: '275 TOPS inference, fanless operation, −40°C to 70°C' },
                  { icon: '🔌', title: 'Hardwired 24V I/O', desc: 'Encoder trigger in, pneumatic reject out — no network jitter' },
                  { icon: '📡', title: 'PROFINET / OPC UA', desc: 'Native PLC integration for machine parameter correlation' },
                ].map((spec) => (
                  <div className="device-spec" key={spec.title}>
                    <div className="device-spec-icon">{spec.icon}</div>
                    <div className="device-spec-text">
                      <h4>{spec.title}</h4>
                      <p>{spec.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. PRODUCT PROOF — The dashboard ───────────────── */}
      <section className="landing-section alt-bg">
        <div className="section-inner product-proof">
          <div className="section-eyebrow">The Analytics Platform</div>
          <h2 className="section-heading">
            See every defect. Trace every batch. Predict every drift.
          </h2>
          <p className="section-subheading" style={{ margin: '0 auto' }}>
            The SealVision web platform gives your QA team full visibility —
            real-time yield monitoring, defect heatmaps showing exactly where
            contamination occurred, and trend analysis that predicts seal
            degradation before it causes a batch failure.
          </p>
          <div className="product-proof-image">
            <img
              src="/images/product-dashboard.jpg"
              alt="SealVision analytics dashboard showing real-time yield metrics at 99.7%, defect distribution charts, and inspection history with pass/fail indicators"
              width="960"
              height="540"
            />
          </div>
        </div>
      </section>

      {/* ── 7. TRUST — Proof that it works ─────────────────── */}
      <section className="landing-section">
        <div className="section-inner">
          <div className="trust-split">
            <div>
              <img
                src="/images/trust-qa-manager.jpg"
                alt="Quality assurance manager reviewing seal inspection data on a tablet, confident in the monitoring system running on her production line"
                width="600"
                height="400"
              />
            </div>
            <div>
              <div className="section-eyebrow">Why Teams Trust SealVision</div>
              <h2 className="section-heading">
                Built for the people who sign off on every batch
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: 8 }}>
                SealVision doesn't claim to replace deterministic leak testing —
                it fills the gap between your ASTM F2338 vacuum decay checks and
                your production speed. Here's what changes when it's on your line:
              </p>
              <ul className="trust-points">
                <li>
                  <span className="trust-check">✓</span>
                  <span>Every rejected package is logged with thermal evidence — no more "when did this start?" during batch investigations.</span>
                </li>
                <li>
                  <span className="trust-check">✓</span>
                  <span>Gradual seal temperature drift is detected days before it causes failures — enabling planned maintenance instead of emergency downtime.</span>
                </li>
                <li>
                  <span className="trust-check">✓</span>
                  <span>False rejects drop from 3–5% to below 0.3% — because thermal anomaly detection doesn't get confused by transparent films or reflective surfaces.</span>
                </li>
                <li>
                  <span className="trust-check">✓</span>
                  <span>ROI payback in under 4 months through reduced scrap, minimized destructive testing, and elimination of unplanned batch quarantines.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. WHERE IT APPLIES ────────────────────────────── */}
      <section className="landing-section alt-bg">
        <div className="section-inner">
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-10)' }}>
            <div className="section-eyebrow">Where SealVision Works</div>
            <h2 className="section-heading">
              Purpose-built for food & beverage packaging
            </h2>
          </div>
          <div className="use-cases-grid">
            {[
              {
                title: 'Thermoforming',
                speed: '60–200 ppm',
                materials: 'PET / PA / PE films',
                desc: 'Uniform planar seals with high dwell time — ideal thermal capture conditions.',
              },
              {
                title: 'VFFS (Bags & Pouches)',
                speed: '80–150 ppm',
                materials: 'Laminates, PE, PP',
                desc: 'End-seals clearly visible post-jaw release; thermal profile easily captured.',
              },
              {
                title: 'Tray Sealing (MAP)',
                speed: '40–150 ppm',
                materials: 'PP / APET / Foil lidding',
                desc: 'Critical for shelf life; seal contamination from meat and cheese is frequent.',
              },
            ].map((uc) => (
              <div className="use-case-card" key={uc.title}>
                <h3>{uc.title}</h3>
                <div style={{ color: 'var(--accent-primary)', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>
                  {uc.speed}
                </div>
                <div className="detail">{uc.materials}</div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
                  {uc.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. OUTCOME / CTA ───────────────────────────────── */}
      <section className="landing-cta">
        <h2>Stop flying blind between QA checks.</h2>
        <p>
          Deploy SealVision on your line and know — with evidence — that every
          seal leaving your facility was inspected.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {user?.authenticated ? (
            <Link to={targetUrl} className="btn btn-primary btn-lg">
              {user.user?.organizationId ? 'Go to Dashboard' : 'Complete Setup'}
            </Link>
          ) : (
            <a href="/auth/login" className="btn btn-primary btn-lg">
              Get Started — Free to Try
            </a>
          )}
          <a href="#how-it-works" className="btn btn-secondary btn-lg">
            See How It Works
          </a>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className="landing-footer">
        <p>© 2026 SealVision by Sensifai. Inline seal quality monitoring for food & beverage manufacturers.</p>
        <p>
          SealVision is an inline seal quality and process stability monitor.
          It does not replace deterministic leak testing (ASTM F2338).
        </p>
      </footer>
    </div>
  );
}
